/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ArchiveNodeConfigResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type ArchiveNodeConfigRequest = {
  configId: string;
};

export type ArchiveNodeConfigMutationConfig = {
  onSuccess?: (data: ArchiveNodeConfigResponse, variables: ArchiveNodeConfigRequest) => void;
  onError: (error: unknown, variables: ArchiveNodeConfigRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: ArchiveNodeConfigRequest) => void;
};

/**
 * Hook for archiving a node configuration through the management plane API.
 *
 * @param config.configId - ID of the configuration to archive
 * @param config.onSuccess - Called after successful configuration archive
 * @param config.onError - Called if archive fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for archiving node configuration
 *
 * @example
 * ```tsx
 * const { mutate } = useArchiveNodeConfigMutation({
 *   configId: '456',
 *   onSuccess: (data) => console.log('Configuration archived:', data),
 *   onError: (error) => console.error('Archive failed:', error)
 * });
 *
 * // Archive configuration
 * mutate({ configId: '456' });
 * ```
 */
export function useArchiveNodeConfigMutation({ onSuccess, onError, onSettled }: ArchiveNodeConfigMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<ArchiveNodeConfigResponse, ApiError, ArchiveNodeConfigRequest>({
    mutationFn: ({ configId }) => {
      return mgmtPlaneAPIClient.nodeConfigs.archiveNodeConfig({
        tenantId: tenantId!,
        configId,
      });
    },
    onSuccess,
    onError,
    onSettled,
  });

  return { mutate, isError, error };
}
